<template>
  <div class="dropdown-with-toggle-root">
    <div class="dropdown-with-toggle-label">
      {{ label }}
    </div>
    <Dropdown
      class="dropdown-with-toggle"
      :placeholder="activePlaceholder"
    >
      <div class="dropdown-with-toggle--header">
        <div class="dropdown-tabs-content">
          <div
            v-for="tab in tabItems"
            :key="tab.value"
            :class="[
              'dropdown-tab-item',
              { active: activeTabItem === tab.value },
            ]"
            @click="onTabItemChange(tab.value)"
          >
            {{ tab.label }}
          </div>
        </div>
        <div class="dropdown-with-toggle--header__search">
          <TextInput
            class="dropdown-with-toggle--header__search-input"
            :model-value="searchValue"
            @update:modelValue="updateSearch"
            icon="search"
            placeholder="Search by name"
            small
          />
        </div>
      </div>
      <div
        v-if="!selectedTabDropdownItems.length"
        class="no-dropdown-items-message"
      >
        No items available
      </div>
      <DropdownItem
        v-for="item in selectedTabDropdownItems"
        :key="item.value"
        @click="onDropdownItemSelect(item)"
        clickable
      >
        {{ item.label }}
        <Icon
          v-if="modelValue?.value === item.value"
          name="check"
        />
      </DropdownItem>
    </Dropdown>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import { toLower, filter, includes } from 'lodash';
import Dropdown from '@/components/common/Dropdown';
import DropdownItem from '@/components/common/DropdownItem';
import Icon from '@/components/common/Icon';
import TextInput from '@/components/common/TextInput';

export default {
  emits: ['update:modelValue'],
  components: {
    Dropdown,
    DropdownItem,
    Icon,
    TextInput,
  },
  props: {
    modelValue: {
      type: [String, Number, Object],
      default: null,
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    // Example [{ label: 'All', value: 'all' }]
    tabItems: {
      type: Array,
      default: () => [],
    },
    /*
      Example:
        {
          tabItemValue: [],
          tabItemValue2: [],
          ...
        }
    */
    dropdownItems: {
      type: Object,
      default: () => {},
    },
  },
  setup(props, { emit }) {
    const activeTabItem = ref(props.tabItems[0].value);
    const activePlaceholder = computed(() => props.modelValue?.label || props.placeholder);
    const searchValue = ref('');
    const selectedTabDropdownItems = computed(() => filter(
      (props.dropdownItems[activeTabItem.value] || []),
      (item) => includes(toLower(item.label), toLower(searchValue.value)),
    ));

    const onTabItemChange = (value) => {
      activeTabItem.value = value;
    };
    const updateSearch = (value) => {
      searchValue.value = value;
    };

    const onDropdownItemSelect = (item) => {
      emit('update:modelValue', item);
    };
    return {
      activeTabItem,
      onTabItemChange,
      activePlaceholder,
      selectedTabDropdownItems,
      onDropdownItemSelect,
      updateSearch,
      searchValue,
    };
  },
};
</script>

<style lang="scss">
.dropdown-with-toggle-root {
  display: flex;
  flex-direction: column;
  gap: 4px;
  .dropdown-menu {
    width: 100%;
    max-height: 310px;
    overflow-y: auto;
  }
  .dropdown-with-toggle-label {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    color: $gray800;
  }
  .dropdown-with-toggle {
    .dropdown-with-toggle--header {
      display: flex;
      flex-direction: column;
      gap: 8px;
      position: sticky;
      top: 0;
      background-color: $white;
      padding: 4px 8px;
      .dropdown-tabs-content {
        display: flex;
        justify-content: space-between;
        gap: 8px;
        border-radius: 4px;
        .dropdown-tab-item {
          width: 50%;
          font-size: 12px;
          font-weight: 500;
          color: $gray800;
          cursor: pointer;
          border-bottom: 2px solid transparent;
          padding: 8px;
          text-align: center;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          &.active {
            border-bottom: 2px solid $brandPrimary500;
          }
        }
      }
    }
    .no-dropdown-items-message {
      padding: 16px 8px;
      font-size: 12px;
      color: $gray800;
      text-align: center;
    }
    .dropdown-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      min-height: 0;
      flex: 0 0 44px;
      .icon {
        svg path {
          stroke: $black;
        }
      }
    }
  }
}
</style>
